<script setup lang="ts">
import type { ReportColumn } from '../../models/column.types'
import type {
  FilterDropdownables,
  FilterMatchMode,
  ReportFilter,
  SavedReportFilterInput,
  SavedReportFilterOption,
} from '../../models/filter.types'

import FilterChip from './FilterChip.vue'
import FilterPopup from './FilterPopup.vue'

import { useI18n } from '@/shared/i18n'
import { Icon, Popover } from '@/shared/ui'

const props = defineProps<{
  filters: readonly ReportFilter[]
  columns: readonly ReportColumn[]
  savedFilters: readonly SavedReportFilterOption[]
  savedFilterIds: readonly (string | undefined)[]
  dropdownables: FilterDropdownables
  matchModes: readonly FilterMatchMode[]
  variant: 'default' | 'standalone'
  disabled: boolean
  numericMode: FilterMatchMode
  checkChange: boolean
  isExport: boolean
  external?: boolean
  onLoadDropdownValues?: ((field: string) => Promise<unknown> | unknown) | undefined
}>()
const emit = defineEmits<{
  apply: [index: number, filter: ReportFilter]
  save: [index: number, saved: SavedReportFilterInput]
  remove: [index: number]
  removeSaved: [id: string]
}>()

const { t } = useI18n()

function savedFor(index: number): SavedReportFilterOption | undefined {
  const id = props.savedFilterIds[index]
  return id ? props.savedFilters.find((saved) => saved.id === id) : undefined
}
</script>

<template>
  <div
    class="report-filter-chips flex flex-wrap items-center gap-sm"
    :class="{ 'report-filter-chips--external': external }"
  >
    <span
      v-for="(filter, index) in filters"
      :key="`${filter.field}-${filter.match_mode}-${index}`"
      data-pdf-filter-chip
      :class="[
        variant === 'standalone'
          ? 'h-7 bg-info/10 text-primary rounded-md'
          : 'h-6 bg-primary-subtle text-primary rounded-sm',
        'flex items-center gap-2 text-sm p-2',
      ]"
    >
      <Popover
        placement="bottom-start"
        :trigger-label="t('reports.filters.editCriterion', { index: index + 1 })"
        :panel-label="t('reports.filters.editCriterion', { index: index + 1 })"
        trigger-variant="plain"
        panel-variant="plain"
        :disabled="disabled"
      >
        <template #trigger>
          <FilterChip
            :filter="filter"
            :column="columns.find((column) => column.field === filter.field)"
          />
        </template>
        <template #default="{ close }">
          <FilterPopup
            :columns="columns"
            :model-value="filter"
            :dropdownables="dropdownables"
            :match-modes="matchModes"
            :numeric-mode="numericMode"
            :check-change="checkChange"
            :is-export="isExport"
            :saved-id="savedFor(index)?.id"
            :saved-name="savedFor(index)?.name"
            :on-load-dropdown-values="onLoadDropdownValues"
            @apply="
              (value) => {
                emit('apply', index, value)
                close()
              }
            "
            @save="emit('save', index, $event)"
            @remove="
              (id) => {
                emit('removeSaved', id)
                close()
              }
            "
            @close="close"
          />
        </template>
      </Popover>
      <button
        v-if="!disabled"
        type="button"
        class="report-filters__remove report-filter-chips__remove inline-flex p-0 border-0 bg-transparent text-inherit cursor-pointer"
        :aria-label="t('reports.filters.removeCriterion', { index: index + 1 })"
        @click="emit('remove', index)"
      >
        <Icon name="x" :size="12" />
      </button>
    </span>
  </div>
</template>

<style scoped>
.report-filter-chips.report-filter-chips--external {
  display: contents;
}
</style>
